import * as THREE from "three";
import { defineModel, styleV3, type Part } from "../../lib";

// A WWI supply lorry: open cab (just a bench and a steering wheel, no
// windscreen box), a boxy khaki bonnet with a gunmetal radiator, spoked
// wheels, and a canvas-covered cargo bed whose rear edge stays open — two
// cargo sockets sit there for carriable goods. Front is +z.

const { box, cyl } = styleV3;

const KHAKI = "#7a6f4e";
const OLIVE = "#5f6b3c";
const GUNMETAL = "#4a4d52";
const CANVAS = "#cbbd93";
const TYRE = "#33342f";

// A spoked wheel standing in the y-z plane (axle along x): a dark tyre disc,
// two crossed spokes proud of the disc and a hub.
function wheel(x: number, z: number, r: number): Part[] {
  return [
    cyl(r, r, 0.06, 12, TYRE, [x, r, z], [0, 0, Math.PI / 2]),
    box(0.075, r * 1.65, 0.028, KHAKI, [x, r, z], [Math.PI / 4, 0, 0]),
    box(0.075, r * 1.65, 0.028, KHAKI, [x, r, z], [-Math.PI / 4, 0, 0]),
    cyl(r * 0.28, r * 0.28, 0.09, 8, GUNMETAL, [x, r, z], [0, 0, Math.PI / 2]),
  ];
}

function parts(): Part[] {
  const R = 0.14;
  const out: Part[] = [
    ...wheel(-0.235, 0.52, R),
    ...wheel(0.235, 0.52, R),
    ...wheel(-0.235, -0.42, R),
    ...wheel(0.235, -0.42, R),
    // axles
    cyl(0.02, 0.02, 0.47, 6, GUNMETAL, [0, R, 0.52], [0, 0, Math.PI / 2]),
    cyl(0.02, 0.02, 0.47, 6, GUNMETAL, [0, R, -0.42], [0, 0, Math.PI / 2]),
    // chassis frame
    box(0.34, 0.05, 1.5, GUNMETAL, [0, 0.18, -0.03]),

    // -- Bonnet and radiator -------------------------------------------------
    box(0.3, 0.18, 0.4, KHAKI, [0, 0.34, 0.55]),
    box(0.32, 0.22, 0.04, GUNMETAL, [0, 0.32, 0.76]),
    // front mudguards
    box(0.08, 0.025, 0.36, OLIVE, [-0.215, 0.31, 0.52]),
    box(0.08, 0.025, 0.36, OLIVE, [0.215, 0.31, 0.52]),

    // -- Open cab: floor, bench, steering wheel ------------------------------
    box(0.4, 0.04, 0.34, KHAKI, [0, 0.22, 0.16]),
    box(0.34, 0.1, 0.06, KHAKI, [0, 0.4, 0.32]), // cowl behind the bonnet
    box(0.38, 0.06, 0.15, OLIVE, [0, 0.3, 0.08]), // bench cushion
    box(0.38, 0.14, 0.04, OLIVE, [0, 0.38, 0.005]), // bench backrest
    cyl(0.012, 0.012, 0.2, 6, GUNMETAL, [0.11, 0.37, 0.25], [0.9, 0, 0]),
    cyl(0.05, 0.05, 0.018, 10, TYRE, [0.11, 0.445, 0.185], [0.9, 0, 0]),

    // -- Cargo bed with a rounded canvas top; rear edge open -----------------
    box(0.48, 0.05, 0.85, KHAKI, [0, 0.3, -0.42]),
    box(0.48, 0.18, 0.03, OLIVE, [0, 0.42, -0.01]), // headboard behind the cab
    box(0.03, 0.1, 0.85, OLIVE, [-0.225, 0.375, -0.42]),
    box(0.03, 0.1, 0.85, OLIVE, [0.225, 0.375, -0.42]),
    box(0.48, 0.07, 0.03, OLIVE, [0, 0.36, -0.83]), // low tailgate
    // canvas tilt: a flattened cylinder lying along z over the front of the bed
    {
      geometry: new THREE.CylinderGeometry(0.24, 0.24, 0.6, 12),
      color: CANVAS,
      position: [0, 0.42, -0.33],
      rotation: [Math.PI / 2, 0, 0],
      scale: [1, 1, 0.72],
    },
  ];
  return out;
}

export const supplyTruck = defineModel({
  id: "supply-truck",
  name: "Supply Truck",
  batchId: "wwi",
  tags: ["vehicle", "wwi", "military", "truck", "lorry", "transport", "cargo"],
  summary: "A khaki WWI lorry with an open cab and a canvas-covered cargo bed.",
  description:
    "A WWI supply lorry: boxy bonnet and radiator, an open cab with a bench " +
    "and steering wheel, spoked wheels and a canvas-covered cargo bed. The " +
    "rear edge of the bed stays open and publishes two cargo sockets for " +
    "carriable goods.",
  author: "Fable 5",
  footprint: 0.7,
  tiles: { w: 1, h: 2 },
  parts,
  animations: [],
  sockets: [
    {
      id: "bed-l",
      position: [-0.12, 0.325, -0.7],
      scale: 0.65,
      slotType: "carry",
      accepts: ["carriable"],
      description: "Open rear bed cargo slot, left.",
    },
    {
      id: "bed-r",
      position: [0.12, 0.325, -0.7],
      scale: 0.65,
      slotType: "carry",
      accepts: ["carriable"],
      description: "Open rear bed cargo slot, right.",
    },
  ],
});
